import { Alert, Box, Chip, FormControlLabel, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";

import { formatValue, workspaceLabelOf } from "./evidenceFormat";
import { IFilters } from "./groupFindings";
import { IFieldChange, IInspection, IInspectionDiff, LENS_LABEL, Lens, SECTION_LABEL, SEVERITY_LABEL, Severity } from "./raio-x.types";

const fmt = (iso: string) => new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" }).format(new Date(iso));

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <Box sx={{ minWidth: 120, p: 1.5, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

// why: "Contrato" só fazia sentido para WORKSPACE; ORGANIZATION e ADDRESSABLE_GROUP não são um contrato, então o rótulo mentia sobre o alvo real da mudança.
function targetLabel(c: IFieldChange, workspaceLabels: Record<string, string>): string {
  if (c.targetType === "ORGANIZATION") return "CNPJ";
  if (c.targetType === "ADDRESSABLE_GROUP") {
    return c.productLine ? `Grupo de endereçabilidade (${SECTION_LABEL[c.productLine]})` : "Grupo de endereçabilidade";
  }
  return workspaceLabelOf(c.targetId, workspaceLabels);
}

// why: chave = último segmento de c.path (ex.: "payload.rules.rulesRegister.vigency" -> "vigency"), a mesma
// convenção de evidenceChangeLine, para o valor ganhar rótulo de enum/data/contrato certo. undefined (campo que
// não existia antes/depois) mostra "(ausente)": o relatório já diz isso desde a task 1, e a tela tem de dizer o
// mesmo; valor presente (mesmo null) passa por formatValue para nunca imprimir JSON cru ("cpf" com aspas),
// "[object Object]" ou "true"/"false".
function lastSegment(path: string): string {
  const segments = path.split(".");
  return segments[segments.length - 1];
}

const cell = (v: unknown, key: string, workspaceLabels: Record<string, string>): string =>
  v === undefined ? "(ausente)" : formatValue(v, key, { workspaceLabels });

export function RaioXSummary({
  inspection,
  diff,
  filters,
  onFiltersChange,
  workspaceLabels,
}: {
  inspection: IInspection;
  diff: IInspectionDiff;
  filters: IFilters;
  onFiltersChange: (f: IFilters) => void;
  workspaceLabels: Record<string, string>;
}) {
  const count = (s: "NEW" | "PERSISTENT" | "RESOLVED" | "NOT_EVALUATED") => diff.findings.filter((f) => f.status === s).length;
  // why: F6 -- "novos" só chama atenção (warning) quando algum achado novo é PRESCRITIVO; descritivo novo é "para conferência", não um problema.
  const newFingerprints = new Set(diff.findings.filter((f) => f.status === "NEW").map((f) => f.fingerprint));
  const hasNewPrescriptive = inspection.targets.some((t) => t.findings.some((f) => newFingerprints.has(f.fingerprint) && f.nature === "PRESCRIPTIVE"));
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">Inspeção de {fmt(inspection.startedAt)} · catálogo {inspection.catalogVersion}</Typography>
      {diff.catalogChanged && (
        <Alert severity="warning">
          O catálogo de verificações mudou de {diff.catalogChanged.from} para {diff.catalogChanged.to} desde a última inspeção: achados resolvidos podem refletir a régua nova, não uma correção na configuração.
        </Alert>
      )}
      {diff.engineChanged && (
        <Alert severity="warning">
          O motor de verificações mudou de {diff.engineChanged.from} para {diff.engineChanged.to} desde a última inspeção: achados resolvidos podem refletir o motor novo, não uma correção na configuração.
        </Alert>
      )}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Kpi label="Bloqueiam" value={inspection.summary.bySeverity.BLOCKER} />
        <Kpi label="Importantes" value={inspection.summary.bySeverity.MAJOR} />
        <Kpi label="Menores" value={inspection.summary.bySeverity.MINOR} />
        <Kpi label="Para conferência" value={inspection.summary.descriptiveCount} />
      </Stack>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Desde a inspeção anterior</Typography>
        {diff.previousInspectionId === null ? (
          <Typography variant="body2" color="text.secondary">Primeira inspeção deste CNPJ.</Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${count("NEW")} novos`} color={hasNewPrescriptive ? "warning" : "default"} />
            <Chip size="small" label={`${count("RESOLVED")} resolvidos`} color={count("RESOLVED") > 0 ? "success" : "default"} />
            <Chip size="small" label={`${count("PERSISTENT")} persistem`} />
            <Chip size="small" label={`${count("NOT_EVALUATED")} não avaliados`} />
            <Chip size="small" variant="outlined" label={`${diff.fieldChanges.length} campos alterados`} />
          </Stack>
        )}
        {diff.fieldChanges.length > 0 && (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Alvo</TableCell>
                <TableCell>Produto</TableCell>
                <TableCell>Campo</TableCell>
                <TableCell>Antes</TableCell>
                <TableCell>Depois</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diff.fieldChanges.map((c) => (
                <TableRow key={`${c.targetId}:${c.productLine ?? ""}:${c.path}`}>
                  <TableCell>{targetLabel(c, workspaceLabels)}</TableCell>
                  {/* why: grupo misto (saúde + odonto) gera dois alvos com o mesmo targetId; sem esta coluna a linha fica ambígua. */}
                  <TableCell>{c.productLine ? SECTION_LABEL[c.productLine] : ""}</TableCell>
                  <TableCell>{c.path}</TableCell>
                  <TableCell>{cell(c.before, lastSegment(c.path), workspaceLabels)}</TableCell>
                  <TableCell>{cell(c.after, lastSegment(c.path), workspaceLabels)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Lente" value={filters.lens ?? ""} onChange={(e) => onFiltersChange({ ...filters, lens: (e.target.value || null) as Lens | null })} sx={{ minWidth: 180 }} SelectProps={{ displayEmpty: true }}>
          <MenuItem value="">Todas</MenuItem>
          {(Object.keys(LENS_LABEL) as Lens[]).map((l) => <MenuItem key={l} value={l}>{LENS_LABEL[l]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Severidade" value={filters.severity ?? ""} onChange={(e) => onFiltersChange({ ...filters, severity: (e.target.value || null) as Severity | null })} sx={{ minWidth: 160 }} SelectProps={{ displayEmpty: true }}>
          <MenuItem value="">Todas</MenuItem>
          {(Object.keys(SEVERITY_LABEL) as Severity[]).map((s) => <MenuItem key={s} value={s}>{SEVERITY_LABEL[s]}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={filters.onlyNew} onChange={(e) => onFiltersChange({ ...filters, onlyNew: e.target.checked })} />} label="Só novos" />
      </Stack>
    </Stack>
  );
}
